import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ThemeProvider } from './theme/ThemeProvider';
import { DateProvider } from './context/DateContext';
import { MetadataProvider } from './context/MetadataContext';
import { isAuthEnabled, loginRequest } from './auth/authConfig';
import { initializeMsalContext } from './services/apiService';
import Header from './components/Header';
import Footer from './components/Footer';
 
function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
 
  useEffect(() => {
    // If authentication is enabled and user is not authenticated, trigger login
    if (isAuthEnabled && !isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch((error) => {
        console.error('Login failed:', error);
      });
    }
  }, [isAuthenticated, inProgress, instance]);
 
  useEffect(() => {
    // Initialize MSAL context for API calls when authenticated
    if (isAuthEnabled && isAuthenticated && accounts.length > 0) {
      initializeMsalContext(instance, accounts);
    }
  }, [isAuthenticated, instance, accounts]);
 
  // Show loading spinner while authentication is in progress
  if (isAuthEnabled && inProgress !== InteractionStatus.None) {
    return (
      <ThemeProvider>
        <MetadataProvider>
          <DateProvider>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
              }}
            >
              <CircularProgress />
            </Box>
          </DateProvider>
        </MetadataProvider>
      </ThemeProvider>
    );
  }
 
  // If auth is enabled but user is not authenticated, show loading (will redirect)
  if (isAuthEnabled && !isAuthenticated) {
    return (
      <ThemeProvider>
        <MetadataProvider>
          <DateProvider>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
              }}
            >
              <CircularProgress />
            </Box>
          </DateProvider>
        </MetadataProvider>
      </ThemeProvider>
    );
  }
 
  return (
    <ThemeProvider>
      <MetadataProvider>
        <DateProvider>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
            }}
          >
            <Header />
            <Box
              component="main"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
              }}
            >
              <Outlet />
            </Box>
            <Footer />
          </Box>
        </DateProvider>
      </MetadataProvider>
    </ThemeProvider>
  );
}
 
export default App